import type { Logger } from 'pino'
import type { CaptureResult } from 'posthog-js'
import 'vanilla-cookieconsent/dist/cookieconsent.css'
import * as CookieConsent from 'vanilla-cookieconsent'
import Plausible, { type EventOptions } from 'plausible-tracker'
import PostHog from 'posthog-js'
import { logger } from './logger'

const COOKIE_CATEGORY_ANALYTICS = 'analytics'
const COOKIE_CATEGORY_ADVERTISING = 'advertising'

const GOOGLE_CONSENT_AD_STORAGE = 'ad_storage'
const GOOGLE_CONSENT_AD_USER_DATA = 'ad_user_data'
const GOOGLE_CONSENT_AD_PERSONALIZATION = 'ad_personalization'
const GOOGLE_CONSENT_ANALYTICS_STORAGE = 'analytics_storage'
// Necessary-tier Google Consent Mode signals - always granted to match the
// default-granted state set in +onRenderHtml.js. These are not gated by a
// vanilla-cookieconsent category; they cover basic UI state preservation
// (functionality), default personalization, and security/CSRF/fraud
// detection.
const GOOGLE_CONSENT_FUNCTIONALITY_STORAGE = 'functionality_storage'
const GOOGLE_CONSENT_PERSONALIZATION_STORAGE = 'personalization_storage'
const GOOGLE_CONSENT_SECURITY_STORAGE = 'security_storage'

export type AdvertisingIdentity = {
  emailHash: string
}

type PlausibleReturn = ReturnType<typeof Plausible>

type PlausibleTrackProps = NonNullable<EventOptions['props']>

type TrackerWindow = Window & {
  fbq?: (...args: unknown[]) => void
  gtag?: (...args: unknown[]) => void
  lintrk?: (...args: unknown[]) => void
}

interface TrackerOptions {
  cookieConsent?: typeof CookieConsent
  dev: boolean
  facebookPixelId?: string
  googleAdsId?: string
  logger?: Logger
  plausible?: PlausibleReturn
  posthog?: typeof PostHog
  window?: TrackerWindow
}

export class Tracker implements TrackerOptions {
  cookieConsent?: typeof CookieConsent
  dev: boolean
  facebookPixelId?: string
  googleAdsId?: string
  logger?: Logger
  plausible?: PlausibleReturn
  posthog?: typeof PostHog
  window?: TrackerWindow

  private posthogPersistence?: 'localStorage+cookie' | 'memory'

  constructor(options: TrackerOptions = { dev: false }) {
    this.cookieConsent = options.cookieConsent
    this.dev = options.dev
    this.facebookPixelId = options.facebookPixelId
    this.googleAdsId = options.googleAdsId
    this.logger = options.logger
    this.plausible = options.plausible
    this.posthog = options.posthog
    this.window = options.window

    // PostHog is initialized in 'memory' mode at module load (see
    // createTracker below). Track that initial state so
    // updatePostHogPersistence only fires posthog.set_config when the value
    // actually changes.
    if (this.posthog) {
      this.posthogPersistence = 'memory'
    }
  }

  // ---------------------------------------------------------------------
  // Lazy global getters - read from this.window at call time so delayed
  // data-category scripts (Facebook, LinkedIn, ZoomInfo, HubSpot) are
  // discovered after they activate, even if Tracker was constructed before
  // vanilla-cookieconsent had the chance to enable them.
  // ---------------------------------------------------------------------

  private getFbq(): ((...args: unknown[]) => void) | undefined {
    return this.window?.fbq
  }

  private getGtag(): ((...args: unknown[]) => void) | undefined {
    return this.window?.gtag
  }

  private getLintrk(): ((...args: unknown[]) => void) | undefined {
    return this.window?.lintrk
  }

  // ---------------------------------------------------------------------
  // Consent helpers - return false when no cookieConsent is injected,
  // which is the SSR shape (constructed via `new Tracker()` with no
  // options).
  // ---------------------------------------------------------------------

  hasAnalyticsConsent(): boolean {
    return this.cookieConsent
      ? this.cookieConsent.acceptedCategory(COOKIE_CATEGORY_ANALYTICS)
      : false
  }

  hasAdvertisingConsent(): boolean {
    return this.cookieConsent
      ? this.cookieConsent.acceptedCategory(COOKIE_CATEGORY_ADVERTISING)
      : false
  }

  // ---------------------------------------------------------------------
  // Vendor methods - each self-gates on consent + global presence so
  // callers can fire-and-forget without external consent checks.
  // ---------------------------------------------------------------------

  event(event: string, props = {}) {
    const { dev, logger, plausible } = this
    const fbq = this.getFbq()
    const gtag = this.getGtag()

    if (!event) return
    if (dev && logger) logger.info({ event, props }, 'Track event')
    if (plausible) plausible.trackEvent(event, { props })
    if (fbq && this.hasAdvertisingConsent()) fbq('track', event, props)
    if (gtag) gtag('event', event, props)
  }

  facebookEvent(event: string, props: Record<string, unknown> = {}) {
    const { dev, logger } = this
    const fbq = this.getFbq()

    if (!event) return
    if (!this.hasAdvertisingConsent()) return
    if (!fbq) return
    if (dev && logger) logger.info({ event, props }, 'Track Facebook event')
    fbq('track', event, props, {
      eventID: `${Date.now()}.${Math.floor(Math.random() * 9999) + 1}`
    })
  }

  /**
   * Re-init the Facebook pixel with Advanced Matching user data. The pixel
   * is initialized once at page load with no Advanced Matching by the
   * data-category script in +onRenderHtml.js; this call adds the
   * email-hash context that subsequent fbq('track', ...) calls inherit.
   * No-op without advertising consent or a non-null identity, so callers
   * can pass through a precomputed AdvertisingIdentity.
   */
  facebookInit(identity: AdvertisingIdentity | null) {
    const { dev, facebookPixelId, logger } = this
    const fbq = this.getFbq()

    if (!identity) return
    if (!this.hasAdvertisingConsent()) return
    if (!fbq) return
    if (!facebookPixelId) return
    if (dev && logger) logger.info({ identity }, 'Track Facebook init')
    fbq('init', facebookPixelId, { em: identity.emailHash })
  }

  googleAdsConversion(label: string, props: Record<string, unknown> = {}) {
    const { dev, googleAdsId, logger } = this
    const gtag = this.getGtag()

    if (!label) return
    if (!this.hasAdvertisingConsent()) return
    if (!googleAdsId) return
    if (!gtag) return
    if (dev && logger)
      logger.info({ label, props }, 'Track Google Ads conversion')
    gtag('event', 'conversion', {
      ...props,
      send_to: `${googleAdsId}/${label}`
    })
  }

  googleEvent(event: string, props: Record<string, unknown> = {}) {
    const { dev, logger } = this
    const gtag = this.getGtag()

    if (!event) return
    if (dev && logger) logger.info({ event, props }, 'Track Google event')
    if (gtag) gtag('event', event, props)
  }

  linkedinTrack(conversionID: number) {
    const { dev, logger } = this
    const lintrk = this.getLintrk()

    if (!conversionID) return
    if (!this.hasAdvertisingConsent()) return
    if (!lintrk) return
    if (dev && logger)
      logger.info({ conversionID }, 'Track LinkedIn conversion')
    lintrk('track', { conversion_id: conversionID })
  }

  plausibleEvent(
    event: string,
    props: PlausibleTrackProps = {},
    err?: unknown
  ) {
    const { dev, logger, plausible } = this

    if (!event) return
    if (
      err &&
      typeof err === 'object' &&
      err !== null &&
      'message' in err &&
      typeof err.message === 'string'
    ) {
      props = { ...props, message: err.message }
    }
    if (dev && logger) logger.info({ event, props }, 'Track Plausible event')
    if (plausible) {
      plausible.trackEvent(event, {
        props
      })
    }
  }

  /**
   * Capture an anonymous PostHog event. Anonymous capture is allowed
   * regardless of consent because PostHog runs in 'memory' persistence by
   * default; it only switches to 'localStorage+cookie' when analytics
   * consent is granted.
   */
  posthogCapture(event: string, props: Record<string, unknown> = {}) {
    const { dev, logger, posthog } = this

    if (!event) return
    if (!posthog) return
    if (dev && logger) logger.info({ event, props }, 'Track PostHog event')
    posthog.capture(event, props)
  }

  /**
   * Identify a PostHog user. No-op (returns null) without analytics
   * consent. The email hash is computed via getDistinctId() so we never
   * persist or send the raw email as the distinct id. Returns the
   * distinct id so callers that need to forward it (e.g. server-side
   * order metadata) can reuse it without rehashing the email.
   */
  async posthogIdentify(
    email: string | null | undefined,
    props: Record<string, unknown> = {}
  ): Promise<string | null> {
    const { dev, logger, posthog } = this

    if (!email) return null
    if (!posthog) return null
    if (!this.hasAnalyticsConsent()) return null

    let distinctId: string | null = null
    try {
      distinctId = await this.getDistinctId(email)
    } catch (err) {
      if (logger) logger.error({ err }, 'getDistinctId error')
      return null
    }
    if (!distinctId) return null
    if (dev && logger)
      logger.info({ distinctId, props }, 'Track PostHog identify')
    posthog.identify(distinctId, { email, ...props })
    return distinctId
  }

  async getDistinctId(email: string | null | undefined) {
    if (!email) return null

    const normalizedEmail = email.toLowerCase().trim()
    const encoder = new TextEncoder()
    const data = encoder.encode(normalizedEmail)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('')
    const truncatedHash = hashHex.slice(0, 20)
    const firstTwoBytes = data.slice(0, 2)
    const hexPrefix = Array.from(firstTwoBytes)
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('')
      .padEnd(4, '0')

    return `di_v1_${hexPrefix}${truncatedHash}`
  }

  async getEmailHash(email: string | null | undefined) {
    if (!email) return null

    const normalizedEmail = email.toLowerCase().trim()
    const encoder = new TextEncoder()
    const data = encoder.encode(normalizedEmail)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('')

    return hashHex
  }

  /**
   * Compute a single advertising identity per user action so callers can
   * reuse the hashed email for both `facebookInit` and any inline
   * `user_data: { email }` props they hand to `googleAdsConversion`.
   * Returns null without advertising consent, with a missing email, or
   * if hashing fails.
   */
  async getAdvertisingIdentity(
    email: string | null | undefined
  ): Promise<AdvertisingIdentity | null> {
    const { logger } = this

    if (!this.hasAdvertisingConsent()) return null

    let emailHash: string | null = null
    try {
      emailHash = await this.getEmailHash(email)
    } catch (err) {
      if (logger) logger.error({ err }, 'getEmailHash error')
      return null
    }
    return emailHash ? { emailHash } : null
  }

  /**
   * Returns the GA client ID, or null when analytics consent is missing,
   * gtag is unavailable, the call times out, or the value is not a
   * non-empty string. Never throws so callers can use the return value
   * directly without a try/catch.
   */
  async getGAClientId(): Promise<string | null> {
    const { dev, logger } = this
    const gtag = this.getGtag()
    const gaId = import.meta.env.VITE_GA_TRACKING_ID

    if (!this.hasAnalyticsConsent()) return null
    if (!gtag) return null
    if (!gaId) return null

    return new Promise<string | null>(resolve => {
      const timeout = setTimeout(() => {
        if (dev && logger) logger.warn('gtag get client_id timed out')
        resolve(null)
      }, 500)

      try {
        gtag('get', gaId, 'client_id', (value: unknown) => {
          clearTimeout(timeout)
          if (typeof value === 'string' && value.length > 0) {
            resolve(value)
          } else {
            if (dev && logger)
              logger.warn(`gtag get client_id returned ${typeof value}`)
            resolve(null)
          }
        })
      } catch (err) {
        clearTimeout(timeout)
        if (logger) logger.error({ err }, 'gtag get client_id error')
        resolve(null)
      }
    })
  }

  pageView({ canonicalPaths, headProps }: Vike.PageContext) {
    const { dev, logger, plausible } = this
    const gtag = this.getGtag()

    if (
      dev &&
      logger &&
      canonicalPaths &&
      canonicalPaths.relative &&
      headProps &&
      headProps.title
    )
      logger.info(
        { title: headProps.title, url: canonicalPaths.relative },
        'Track pagevew'
      )

    if (plausible && canonicalPaths && canonicalPaths.absolute)
      plausible.trackPageview({
        url: canonicalPaths.absolute
      })

    if (
      gtag &&
      this.window &&
      canonicalPaths &&
      canonicalPaths.absolute &&
      headProps &&
      headProps.title
    ) {
      const originalURL = new URL(this.window.location.href)
      const canonicalURL = new URL(canonicalPaths.absolute)
      canonicalURL.search = originalURL.search

      gtag('event', 'page_view', {
        page_location: canonicalURL.toString(),
        page_title: headProps.title
      })
    }
  }

  // ---------------------------------------------------------------------
  // Internal consent sync - called from CookieConsent.run callbacks.
  // ---------------------------------------------------------------------

  /**
   * Push the current consent state into Google's gtag Consent Mode. The
   * advertising category drives ad_storage, ad_user_data, and
   * ad_personalization; the analytics category drives analytics_storage.
   * Necessary-tier signals (functionality_storage, personalization_storage,
   * security_storage) stay granted, matching the eager defaults in
   * +onRenderHtml.js.
   */
  updateGtagConsent() {
    const gtag = this.getGtag()
    if (!gtag) return

    const advertising = this.hasAdvertisingConsent()
    const analytics = this.hasAnalyticsConsent()
    const grant = (allowed: boolean) => (allowed ? 'granted' : 'denied')

    gtag('consent', 'update', {
      [GOOGLE_CONSENT_AD_STORAGE]: grant(advertising),
      [GOOGLE_CONSENT_AD_USER_DATA]: grant(advertising),
      [GOOGLE_CONSENT_AD_PERSONALIZATION]: grant(advertising),
      [GOOGLE_CONSENT_ANALYTICS_STORAGE]: grant(analytics),
      [GOOGLE_CONSENT_FUNCTIONALITY_STORAGE]: 'granted',
      [GOOGLE_CONSENT_PERSONALIZATION_STORAGE]: 'granted',
      [GOOGLE_CONSENT_SECURITY_STORAGE]: 'granted'
    })
    gtag('set', 'ads_data_redaction', !advertising)
    gtag('set', 'allow_ad_personalization_signals', advertising)
    gtag('set', 'url_passthrough', false)
  }

  /**
   * Switch PostHog persistence to match the current analytics consent.
   * No-op when persistence already matches the desired value, so it's
   * safe to call repeatedly from onConsent and onChange.
   */
  updatePostHogPersistence() {
    const { posthog } = this
    if (!posthog) return

    const persistence: 'localStorage+cookie' | 'memory' =
      this.hasAnalyticsConsent() ? 'localStorage+cookie' : 'memory'
    if (persistence === this.posthogPersistence) return
    posthog.set_config({ persistence })
    this.posthogPersistence = persistence
  }
}

function createTracker(): Tracker {
  if (import.meta.env.SSR) return new Tracker()

  console.log('Tracker initialization')

  const domain = import.meta.env.VITE_DOMAIN
  const internalUser = window.localStorage.getItem('internal-user')
  const posthogToken = import.meta.env.VITE_POSTHOG_TOKEN
  const posthogHost =
    import.meta.env.VITE_POSTHOG_API_HOST || 'https://us.i.posthog.com'

  const plausible = domain
    ? Plausible({
        domain,
        trackLocalhost: false
      })
    : undefined

  // Initialize PostHog at startup in cookieless ('memory') mode so it can
  // capture anonymous events without setting cookies.
  // updatePostHogPersistence upgrades persistence to 'localStorage+cookie'
  // when analytics consent is granted.
  const posthog = posthogToken
    ? PostHog.init(posthogToken, {
        api_host: posthogHost,
        person_profiles: 'identified_only',
        persistence: 'memory',
        before_send: (event: CaptureResult | null): CaptureResult | null => {
          if (!event) return null

          if (internalUser === 'true') {
            event.properties.is_internal_user = true
          }
          return event
        }
      })
    : undefined

  const t = new Tracker({
    cookieConsent: CookieConsent,
    dev: import.meta.env.DEV,
    facebookPixelId: import.meta.env.VITE_FACEBOOK_PIXEL_ID,
    googleAdsId: import.meta.env.VITE_GOOGLE_ADS_ID,
    logger,
    plausible,
    posthog,
    window: window as TrackerWindow
  })

  function syncConsent() {
    t.updateGtagConsent()
    t.updatePostHogPersistence()
  }

  // Widget pages (flyer embeds under /widgets/) skip the consent UI —
  // same path rule as pageContext.isWidget in +onBeforeRender.js.
  // Without .run(), acceptedCategory stays false so analytics/ads
  // remain cookieless.
  // https://cookieconsent.orestbida.com/reference/api-reference.html
  const isWidget = window.location.pathname.startsWith('/widgets/')
  if (isWidget) return t

  CookieConsent.run({
    onConsent: () => {
      console.log('onConsent')
      syncConsent()
    },
    onChange: () => {
      console.log('onChange')
      syncConsent()
    },

    categories: {
      necessary: {
        enabled: true,
        readOnly: true
      },
      analytics: {},
      advertising: {}
    },

    cookie: {
      name: 'hda-cc-selections',
      useLocalStorage: true
    },

    guiOptions: {
      consentModal: {
        layout: 'cloud inline',
        position: 'top center'
      }
    },

    language: {
      default: 'en',
      translations: {
        en: {
          consentModal: {
            title: 'We value your privacy',
            description:
              'We use cookies and similar technologies to make our site work, understand how visitors use it, and improve your experience. You can choose which categories of cookies to allow.',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            showPreferencesBtn: 'Manage preferences'
          },
          preferencesModal: {
            title: 'Cookie preferences',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            savePreferencesBtn: 'Save preferences',
            closeIconLabel: 'Close modal',
            sections: [
              {
                title: 'Your privacy matters',
                description:
                  'You are in control. The toggles below let you decide which categories of cookies and tracking technologies are active while you browse the site. Strictly necessary cookies are always on; everything else is opt-in. For more details, see our <a href="/legal/privacy-policy">privacy policy</a>.'
              },
              {
                title: 'Strictly necessary',
                description:
                  'These cookies and storage are required for the website to function properly and cannot be turned off. They support core operations such as page navigation, form submission, security, and a privacy-friendly anonymous count of visits (Plausible, PostHog in cookieless mode).',
                linkedCategory: 'necessary'
              },
              {
                title: 'Analytics',
                description:
                  'These cookies help us understand how visitors interact with the site so we can improve it. They are set by Google Analytics, HubSpot, PostHog, and Snitcher and may identify a returning visitor across sessions.',
                linkedCategory: 'analytics'
              },
              {
                title: 'Advertising',
                description:
                  'These cookies are used by our advertising and marketing partners (Google Ads, Meta/Facebook, LinkedIn, ZoomInfo) to measure the effectiveness of campaigns and to show you relevant content on other websites you visit.',
                linkedCategory: 'advertising'
              },
              {
                title: 'More information',
                description:
                  'For questions about our cookie practices or to update your choices later, please see our <a href="/legal/privacy-policy">privacy policy</a> or <a href="/contact">contact us</a>.'
              }
            ]
          }
        }
      }
    }
  })

  return t
}

export const tracker = createTracker()
